import { useGLTF } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { RigidBody, CuboidCollider } from '@react-three/rapier';
import { useRef, useMemo } from 'react'; // Afegit useEffect
import * as THREE from 'three'; // Afegit THREE per THREE.MathUtils

export function Asterisc({ position, id, onCollide, onRemove }) {
  const { scene } = useGLTF('./asterisc.glb', './draco/');
  const clonedScene = useMemo(() => scene.clone(), [scene]);
  
  const rigidbodyRef = useRef(); // Ref per al RigidBody
  const visualRef = useRef(); // Ref per al <primitive> que conté l'objecte visual
  
  const { camera } = useThree();
  const materialsConfiguredRef = useRef(false); // Per assegurar que els materials es configuren només un cop

  useFrame(() => {
    // Primer, gestionem la lògica d'eliminació si l'asterisc està massa lluny
    if (rigidbodyRef.current && onRemove) {
      const asteriscWorldPosForRemoval = rigidbodyRef.current.translation();
      const cameraXForRemoval = camera.position.x;
      if (asteriscWorldPosForRemoval.x < cameraXForRemoval - 20) {
        onRemove(id);
        return; // Si s'elimina, no continuem amb la lògica d'opacitat
      }
    }

    if (!rigidbodyRef.current || !visualRef.current) return;

    if (!materialsConfiguredRef.current) {
      visualRef.current.traverse((child) => {
        if (child.isMesh && child.material) {
          // Clonem el material per a aquesta instància específica.
          // Això evita que totes les instàncies comparteixin i modifiquin el mateix material.
          child.material = child.material.clone();
          child.material.transparent = true;
        }
      });
      materialsConfiguredRef.current = true; // Marquem que els materials s'han configurat.
    }
    
    // Si els materials encara no s'han pogut configurar en aquest cicle, esperem al següent.
    if (!materialsConfiguredRef.current) return;

    // Lògica per a l'efecte de boira/fade
    const asteriscWorldPos = rigidbodyRef.current.translation();
    const cameraX = camera.position.x;
    const distanceToCameraX = asteriscWorldPos.x - cameraX;
    
    // Constants per a l'efecte de boira (ajusta-les segons necessitis)
    const FOG_NEAR_ASTEROID = 20; 
    const FOG_FAR_ASTEROID = 75;  
    const MIN_OPACITY_ASTEROID = 0.1; // Opacitat mínima per als asteriscs llunyans

    let targetOpacity;

    if (distanceToCameraX <= FOG_NEAR_ASTEROID) {
      targetOpacity = 1.0;
    } else if (distanceToCameraX >= FOG_FAR_ASTEROID) {
      targetOpacity = MIN_OPACITY_ASTEROID;
    } else {
      const fadeProgress = (distanceToCameraX - FOG_NEAR_ASTEROID) / (FOG_FAR_ASTEROID - FOG_NEAR_ASTEROID);
      targetOpacity = 1.0 - fadeProgress * (1.0 - MIN_OPACITY_ASTEROID);
    }
    
    targetOpacity = THREE.MathUtils.clamp(targetOpacity, MIN_OPACITY_ASTEROID, 1.0);

    visualRef.current.traverse((child) => {
      if (child.isMesh && child.material && child.material.transparent) {
        child.material.opacity = targetOpacity;
      }
    });
  });

  const colliderArgs = useMemo(() => [0.5, 0.5, 0.5], []); 
  const colliderOffset = useMemo(() => [0.3, 0.5, 0], []); 

  return (
    <RigidBody
      ref={rigidbodyRef} // Canviat de 'ref' a 'rigidbodyRef' per claredat
      type="fixed"
      colliders={false}
      position={position}
      rotation={[0, Math.PI * 0.5, 0]}
      onCollisionEnter={(e) => {
        if (e.other.rigidBodyObject?.userData?.type === 'player') {
          if (onCollide) onCollide(id);
        }
      }}
      userData={{ type: 'asteroid', asteroidId: id }}
    >
      <primitive ref={visualRef} object={clonedScene} scale={1.5} />
      <CuboidCollider
        args={colliderArgs}
        position={colliderOffset}
      />
    </RigidBody>
  );
}